import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../configureStore';

export interface Message {
    message: string;
    sender: string;
    followUpPrompts?: string[];
}

export interface Chat {
    mostRecentMessage: string | null;
    chatHistory: Message[];
    sessionId: string | null;
}

export interface ChatbotState {
    isLoading: boolean;
    isError: boolean;
    mostRecentMessage: string | null;
    messages: Message[];
    sessionId: string | null;
    followUpPrompts?: string[];
}

// Sichere UUID-Generierung für Mobile App Kompatibilität
const generateSafeUUID = (): string => {
    if (typeof crypto !== 'undefined' && crypto !== null && typeof crypto.randomUUID === 'function') {
        try {
            return crypto.randomUUID();
        } catch (error) {
            console.warn('crypto.randomUUID() failed, using fallback');
        }
    }

    // Fallback-Implementierung
    const hexDigits = '0123456789abcdef';
    let uuid = '';
    for (let i = 0; i < 36; i++) {
        if (i === 8 || i === 13 || i === 18 || i === 23) {
            uuid += '-';
        } else if (i === 14) {
            uuid += '4';
        } else if (i === 19) {
            uuid += hexDigits[(Math.random() * 4) | 8];
        } else {
            uuid += hexDigits[(Math.random() * 16) | 0];
        }
    }
    return uuid;
};

const initialState: ChatbotState = {
    isLoading: false,
    isError: false,
    mostRecentMessage: null,
    messages: [{ message: 'Hallo! Wie kann ich Ihnen helfen?', sender: 'chatbot' }],
    sessionId: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : null,
    followUpPrompts: [
        'Wie kann ich eine neue Bestellung aufgeben?',
        'Wie sehe ich den aktuellen Status meiner letzten Bestellung?',
        'Welche Restmenge habe ich noch in meinen aktuellen Kontrakten?',
    ],
};

const chatbotSlice = createSlice({
    name: 'chatbot',
    initialState,
    reducers: {
        // Sends a message and sets the loading state to true
        sendMessage(state, action: PayloadAction<{ message: string; messages: Message[]; sessionId: string }>) {
            return {
                ...state,
                isError: false,
                isLoading: true,
                messages: [...state.messages, { message: action.payload.message, sender: 'user' }],
                mostRecentMessage: action.payload.message,
            };
        },
        // Retry the sending of a message and set the loading state to true
        retryMessage(state, action: PayloadAction<{ message: string; messages: Message[]; sessionId: string }>) {
            return {
                ...state,
                isError: false,
                isLoading: true,
                messages: [...state.messages],
                mostRecentMessage: action.payload.message,
            };
        },
        // Marks message sending as successful, sets loading to false, and adds a message
        sendMessageSuccess(state, action: PayloadAction<Message>) {
            return {
                ...state,
                isError: false,
                isLoading: false,
                messages: [...state.messages, { message: action.payload.message, sender: 'chatbot' }],
                mostRecentMessage: action.payload.message,
                followUpPrompts: action.payload.followUpPrompts,
            };
        },
        // Marks message sending as failed and sets loading to false
        sendMessageFailure(state) {
            return {
                ...state,
                isError: true,
                isLoading: false,
            };
        },
        // Resets the chatbot state
        resetChatbotState() {
            return {
                isLoading: false,
                isError: false,
                mostRecentMessage: null,
                messages: [],
                sessionId: null,
            };
        },
    },
});

// Exporting the actions generated by createSlice
export const { sendMessage, sendMessageSuccess, sendMessageFailure, retryMessage, resetChatbotState } = chatbotSlice.actions;

// Selector to access the chatbot state in the store
export const selectChatbot = (state: RootState): ChatbotState => state.chatbot;

// Exporting the reducer to be included in the root reducer
export const chatbotReducer = chatbotSlice.reducer;
